import React, { useMemo } from 'react';
import type { CompanyDetailsErrors, CompanyDetailsField } from '@shared/utils/giftVoucherBuyer';
import type { ShopTheme } from '../theme';
import { intlLocale, useI18n } from '../i18n';
import { Field } from './FormField';
import { inputStyle } from './formStyles';
import {
  COMPANY_FORM_FIELDS,
  companyErrorKey,
  countryOptions,
  registrationHintKey,
  type CompanyDetailsForm,
} from './companyDetailsForm';

/**
 * A company buyer's details: legal name, VAT and register numbers, address, the email the
 * purchase confirmation goes to, and an optional PO reference. Used at checkout and on the
 * "add company details" page.
 */
const CompanyDetailsFields: React.FC<{
  theme: ShopTheme;
  values: CompanyDetailsForm;
  onChange: (field: CompanyDetailsField, value: string) => void;
  errors: CompanyDetailsErrors;
  /** The venue's ISO-2 country: picks the register hint and tops the country list. */
  venueCountry?: string;
  disabled?: boolean;
}> = ({ theme, values, onChange, errors, venueCountry, disabled }) => {
  const { t, locale } = useI18n();
  const countries = useMemo(() => countryOptions(intlLocale(locale), venueCountry), [locale, venueCountry]);
  const registrationHint = registrationHintKey(venueCountry);

  const hintFor = (field: CompanyDetailsField, presence: string): string | undefined => {
    const parts: string[] = [];
    if (presence === 'optional') parts.push(t('companyDetails.optional'));
    if (presence === 'recommended') parts.push(t('companyDetails.recommended'));
    if (field === 'registration_number' && registrationHint) parts.push(t(registrationHint));
    return parts.length ? `(${parts.join(' · ')})` : undefined;
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="company-fields">
      {COMPANY_FORM_FIELDS.map(({ field, presence, wide, type, autoComplete }) => {
        const errorKey = companyErrorKey(field, errors[field]);
        const invalid = Boolean(errors[field]);
        return (
          <Field
            key={field}
            theme={theme}
            label={t(`companyDetails.${field}`)}
            hint={hintFor(field, presence)}
            error={errorKey ? t(errorKey) : undefined}
            className={wide ? 'sm:col-span-2' : ''}
          >
            {field === 'country' ? (
              <select
                name={`company_${field}`}
                value={values.country}
                autoComplete={autoComplete}
                disabled={disabled}
                aria-invalid={invalid}
                onChange={(e) => onChange(field, e.target.value)}
                style={{ ...inputStyle(theme, invalid), appearance: 'auto' }}
              >
                {!values.country && <option value="">—</option>}
                {countries.preferred.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
                <option disabled value="__separator">
                  ──────────
                </option>
                {countries.others.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={type || 'text'}
                name={`company_${field}`}
                value={values[field]}
                autoComplete={autoComplete}
                disabled={disabled}
                aria-invalid={invalid}
                required={presence === 'required'}
                onChange={(e) => onChange(field, e.target.value)}
                style={inputStyle(theme, invalid)}
              />
            )}
          </Field>
        );
      })}
    </div>
  );
};

export default CompanyDetailsFields;
